import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Flame } from "lucide-react";

export default function Auth({ mode }) {
  const nav = useNavigate();
  const loc = useLocation();
  const { login, register } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);

  const isLogin = mode === "login";
  const titleWord = isLogin ? "Welcome back." : "Create your account.";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
        toast.success("Signed in");
      } else {
        await register(email, password, fullName);
        toast.success("Account created — welcome to APM");
      }
      const next = loc.state?.from?.pathname || "/dashboard";
      nav(next, { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail || "Something went wrong";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white min-h-screen">
      <Navbar />
      <div className="max-w-6xl mx-auto grid lg:grid-cols-2 border-x border-zinc-200 min-h-[calc(100vh-64px)]">
        {/* Form */}
        <div className="flex items-center justify-center px-8 py-16">
          <form onSubmit={submit} className="w-full max-w-sm" data-testid={isLogin ? "login-form" : "register-form"}>
            <div className="overline mb-3"><span className="red-bar" />{isLogin ? "Student login" : "New student"}</div>
            <h1 className="font-heading text-4xl font-bold tracking-tight mb-2">{titleWord}</h1>
            <p className="text-sm text-zinc-600 mb-8">
              {isLogin ? "Pick up where you left off in your NICET Level 1 journey." : "Free to start. Progress is saved automatically as you complete lessons and quizzes."}
            </p>

            {!isLogin && (
              <div className="mb-4">
                <Label htmlFor="full_name" className="text-xs font-mono uppercase tracking-widest text-zinc-600">Full name</Label>
                <Input id="full_name" value={fullName} onChange={(e) => setFullName(e.target.value)} required className="mt-2 rounded-sm" data-testid="register-full-name-input" />
              </div>
            )}
            <div className="mb-4">
              <Label htmlFor="email" className="text-xs font-mono uppercase tracking-widest text-zinc-600">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-2 rounded-sm" data-testid={`${isLogin ? "login" : "register"}-email-input`} />
            </div>
            <div className="mb-6">
              <Label htmlFor="password" className="text-xs font-mono uppercase tracking-widest text-zinc-600">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={6} required className="mt-2 rounded-sm" data-testid={`${isLogin ? "login" : "register"}-password-input`} />
            </div>

            <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid={`${isLogin ? "login" : "register"}-submit-btn`}>
              {loading ? "Please wait…" : isLogin ? "Sign in" : "Create account"}
            </button>

            <p className="text-sm text-zinc-600 mt-6">
              {isLogin ? "No account yet?" : "Already have an account?"}{" "}
              <Link to={isLogin ? "/register" : "/login"} className="font-semibold text-red-600 hover:text-red-700" data-testid="auth-toggle-link">
                {isLogin ? "Enroll now" : "Log in"}
              </Link>
            </p>
          </form>
        </div>

        {/* Side panel */}
        <div className="hidden lg:flex bg-zinc-950 text-white p-12 flex-col justify-between relative overflow-hidden">
          <div>
            <div className="flex items-center gap-2 mb-16">
              <div className="w-8 h-8 bg-red-600 grid place-items-center">
                <Flame className="w-4 h-4 text-white" strokeWidth={2.5} />
              </div>
              <div>
                <div className="font-heading text-sm font-bold">APM Fire Alarm Exam Prep</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-400">Est. 2026</div>
              </div>
            </div>
            <div className="overline text-red-500 mb-3"><span className="red-bar" />The program</div>
            <h2 className="font-heading text-4xl font-bold tracking-tight leading-tight mb-4">
              70 hours. <br />6 modules. <br /><span className="text-red-500">One certification +<br />NYC S-98.</span>
            </h2>
            <p className="text-zinc-400 text-sm leading-relaxed max-w-md">
              Taught to the NICET Level 1 Foundation blueprint with built-in AI tutoring powered by Claude
              Sonnet 4.5. Quiz every lesson, simulate the real exam, and earn a certificate when you pass.
            </p>
          </div>
          <div className="flex items-center gap-8 text-xs text-zinc-500 font-mono uppercase tracking-widest">
            <span>NFPA 72</span>
            <span>·</span>
            <span>UL 864</span>
            <span>·</span>
            <span>NEC 760</span>
          </div>
        </div>
      </div>
    </div>
  );
}
